import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import { CompanyConfig } from '@/constants/cx-companies';

interface CXFeedbackProps {
  config: CompanyConfig;
  callDuration: number;
  onStartNewCall: () => void;
  transcript?: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
}

export const CXFeedback: React.FC<CXFeedbackProps> = ({
  config,
  callDuration,
  onStartNewCall,
  transcript = []
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [wasHelpful, setWasHelpful] = useState<boolean | null>(null);
  const [showAlternatives, setShowAlternatives] = useState(false);

  const handleFeedback = (helpful: boolean) => {
    setWasHelpful(helpful);
    setFeedbackGiven(true);
    if (!helpful) {
      setShowAlternatives(true);
    }
  };

  const handleSendFeedback = () => {
    // In a real app, this would send feedback to backend
    console.log('Feedback sent:', wasHelpful);
    setShowAlternatives(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-blue-600 text-xs md:text-sm">Call end - Feedback</h1>
            <div className="flex items-center gap-2 md:gap-4">
              <img src={config.logo} alt={config.name} className="h-6 md:h-8" />
              <div className="flex items-center gap-1">
                <span className="text-gray-400 text-xs md:text-sm">Powered by</span>
                <img src={config.hupoLogo} alt="Hupo" className="h-5 md:h-6 mt-0.5" />
              </div>
              {/* Duration badge */}
              <div className="bg-purple-600 text-white px-2 md:px-3 py-1 rounded text-xs md:text-sm font-medium">
                {Math.floor(callDuration / 60)}:
                {(callDuration % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-73px)] bg-gray-100">
        <div className="flex flex-col lg:flex-row w-full max-w-7xl mx-auto p-4 lg:p-8 gap-4 lg:gap-8">
          {/* Left side - Feedback */}
          <div className="flex-1 bg-white rounded-2xl shadow-lg p-6 lg:p-12 min-h-[400px] lg:min-h-0">

            {/* Feedback section */}
            {!feedbackGiven ? (
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-6">
                  Was the call helpful?
                </h3>

                <div className="flex justify-center gap-4 mb-8">
                  <button
                    onClick={() => handleFeedback(true)}
                    className="px-8 py-3 rounded-full border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => handleFeedback(false)}
                    className="px-8 py-3 rounded-full border-2 border-gray-300 hover:border-red-500 hover:bg-red-50 transition-colors"
                  >
                    No
                  </button>
                </div>

                <button
                  onClick={handleSendFeedback}
                  className="w-full py-3 rounded-full text-white font-medium transition-opacity hover:opacity-90"
                  style={{ backgroundColor: config.colors.primary }}
                >
                  Send
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-gray-600 mb-6">Thank you for your feedback!</p>
              </div>
            )}

            {/* Start another call button */}
            <button
              onClick={onStartNewCall}
              className="w-full mt-6 py-3 rounded-full border-2 font-medium transition-all hover:shadow-md flex items-center justify-center gap-2"
              style={{
                borderColor: config.colors.primary,
                color: config.colors.primary,
              }}
            >
              <Phone className="w-5 h-5" />
              Start another call
            </button>
          </div>

          {/* Right side - Transcript */}
          <div className="flex-1 bg-white rounded-2xl shadow-lg overflow-hidden min-h-[300px] lg:min-h-0">
            <div className="h-full flex flex-col bg-gray-50">
              {/* Transcript messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {transcript.map((item, index) => (
                  <div
                    key={index}
                    className={`flex ${item.source === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        item.source === 'user'
                          ? 'bg-pink-500 text-white'
                          : 'bg-white text-gray-800 shadow-sm'
                      }`}
                    >
                      <p className="text-sm">{item.message}</p>
                      <p className={`text-xs mt-1 ${
                        item.source === 'user' ? 'text-pink-100' : 'text-gray-500'
                      }`}>
                        {item.timestamp.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Call ended notice */}
              <div className="border-t border-gray-200 p-4 bg-white">
                <p className="text-center text-gray-500 text-sm">This call has ended</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};